import { useCallback, useState } from "react";
import PhorestBooking, { OpenBookingLink } from "@/components/PhorestBooking";
import CallbackForm from "@/components/adlanding/CallbackForm";
import { BOOKING_WIDGETS } from "@/data/adLanding";
import { CLINIC } from "@/data/content";
import { trackEvent } from "@/lib/analytics";

const FormatToggle = ({ format, onChange }) => (
  <div className="grid grid-cols-2 gap-2 mb-5" role="group" aria-label="Assessment format" data-testid="hair-format-toggle">
    {[["inPerson", "In person at Cottesloe"], ["video", "By video"]].map(([key, label]) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={format === key}
        data-testid={`hair-format-${key}`}
        className={`py-3.5 px-3 text-[12px] tracking-[0.12em] uppercase font-medium border transition-colors duration-300 ${
          format === key ? "bg-deep-sea text-warm-white border-deep-sea" : "bg-transparent text-deep-sea border-deep-sea/40 hover:border-deep-sea"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const BookingPanel = ({ page, source, heading, widgetKey, formats, defaultFormat = "inPerson", fifo, practitioner, onInteract, onCallback }) => {
  const [status, setStatus] = useState("loading");
  const [format, setFormat] = useState(defaultFormat);
  const failed = status === "failed";
  const src = BOOKING_WIDGETS[formats ? formats[format] : widgetKey];

  const onLoaded = useCallback(() => trackEvent("booking_widget_loaded", { page, source }), [page, source]);
  const onWidgetInteract = useCallback(() => {
    trackEvent("booking_started", { page, source });
    onInteract?.();
  }, [page, source, onInteract]);

  return (
    <aside id="booking-panel" className="bg-sand/60 border border-hairline/60 p-6 sm:p-10" data-testid="ad-booking-panel">
      <h2 className="font-serif font-light text-deep-sea text-[28px] leading-tight mb-5" data-testid="ad-widget-heading">{heading}</h2>
      {formats && <FormatToggle format={format} onChange={setFormat} />}
      {fifo && (
        <p className={`text-sm leading-relaxed mb-5 px-4 py-3 border ${format === "video" ? "bg-cream border-gold text-ink" : "border-hairline/50 text-ink/70"}`} data-testid="hair-fifo-block">
          {fifo}
        </p>
      )}
      <PhorestBooking status={status} onStatus={setStatus} src={src} lazy height={640} onLoaded={onLoaded} onInteract={onWidgetInteract} />
      {failed ? (
        <p className="text-ink text-sm leading-relaxed bg-cream border border-gold/60 px-4 py-3" data-testid="ad-widget-fallback">
          Online booking is momentarily unavailable — request a callback and we'll book you in.
        </p>
      ) : (
        <OpenBookingLink href={src} className="mt-3" />
      )}
      <div className="border-t border-hairline/50 my-8" />
      <p className="font-serif text-deep-sea text-2xl mb-4" data-testid="ad-callback-heading">Prefer to talk first?</p>
      <CallbackForm page={page} source={source} practitioner={practitioner} onSubmitted={onCallback} />
      <p className="text-ink/75 text-sm mt-5" data-testid="ad-call-line">
        Or call <a href={`tel:${CLINIC.phoneTel}`} className="link-sea">{CLINIC.phone}</a>
      </p>
    </aside>
  );
};

export default BookingPanel;
